import { useEffect, useState, useMemo, Fragment } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, ScrollText } from "lucide-react";

const fmt = (n) => Number(n || 0).toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function Asientos() {
  const { empresa, formatApiError } = useApp();
  const [items, setItems] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [open, setOpen] = useState(false);
  const blankDet = { cuenta_codigo: "", glosa: "", debe: 0, haber: 0 };
  const blank = { fecha: new Date().toISOString().slice(0, 10), glosa: "", tipo: "MANUAL", detalles: [{ ...blankDet }, { ...blankDet }] };
  const [form, setForm] = useState(blank);

  const load = async () => { const { data } = await api.get("/asientos"); setItems(data); };
  useEffect(() => { if (empresa) { load(); api.get("/plan-contable").then((r) => setCuentas(r.data)); } /* eslint-disable-next-line */ }, [empresa]);

  const setDet = (i, f, v) => setForm((p) => { const a = [...p.detalles]; a[i] = { ...a[i], [f]: v }; return { ...p, detalles: a }; });
  const addDet = () => setForm((f) => ({ ...f, detalles: [...f.detalles, { ...blankDet }] }));
  const removeDet = (i) => setForm((f) => ({ ...f, detalles: f.detalles.filter((_, idx) => idx !== i) }));

  const totales = useMemo(() => {
    const debe = form.detalles.reduce((s, d) => s + (+d.debe || 0), 0);
    const haber = form.detalles.reduce((s, d) => s + (+d.haber || 0), 0);
    return { debe, haber, diff: debe - haber };
  }, [form.detalles]);

  const submit = async (e) => {
    e.preventDefault();
    if (Math.abs(totales.diff) > 0.01) { toast.error("Asiento descuadrado"); return; }
    try {
      const payload = { ...form, detalles: form.detalles.map((d) => ({ ...d, debe: +d.debe, haber: +d.haber })) };
      await api.post("/asientos", payload); toast.success("Asiento guardado"); setOpen(false); setForm(blank); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const eliminar = async (a) => { if (!confirm("¿Eliminar asiento?")) return; await api.delete(`/asientos/${a.id}`); toast.success("Eliminado"); load(); };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Asientos Contables</h1>
          <p className="text-[13px] text-slate-500">Libro diario. Los asientos de ventas y compras se generan automáticamente.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(blank); }}>
          <DialogTrigger asChild><Button data-testid="btn-nuevo-asiento" className="h-9 bg-slate-900 text-white"><Plus className="w-4 h-4 mr-1" /> Nuevo asiento</Button></DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Nuevo asiento contable</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-[12px]">Fecha</Label>
                  <Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} className="h-9" />
                </div>
                <div className="col-span-2">
                  <Label className="text-[12px]">Glosa</Label>
                  <Input value={form.glosa} onChange={(e) => setForm({ ...form, glosa: e.target.value })} required className="h-9" />
                </div>
              </div>
              <div className="border border-slate-200 rounded">
                <table className="tbl w-full">
                  <thead><tr><th>Cuenta</th><th>Glosa</th><th className="text-right">Debe</th><th className="text-right">Haber</th><th></th></tr></thead>
                  <tbody>
                    {form.detalles.map((d, i) => (
                      <tr key={i}>
                        <td className="w-56">
                          <Select value={d.cuenta_codigo} onValueChange={(v) => setDet(i, "cuenta_codigo", v)}>
                            <SelectTrigger className="h-8 text-[12px]"><SelectValue placeholder="Cuenta" /></SelectTrigger>
                            <SelectContent>{cuentas.map((c) => <SelectItem key={c.id} value={c.codigo}><span className="mono mr-2">{c.codigo}</span>{c.nombre}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td><Input value={d.glosa} onChange={(e) => setDet(i, "glosa", e.target.value)} className="h-8 text-[12px]" /></td>
                        <td className="w-28"><Input type="number" step="0.01" value={d.debe} onChange={(e) => setDet(i, "debe", e.target.value)} className="h-8 tabular text-right text-[12px]" /></td>
                        <td className="w-28"><Input type="number" step="0.01" value={d.haber} onChange={(e) => setDet(i, "haber", e.target.value)} className="h-8 tabular text-right text-[12px]" /></td>
                        <td><button type="button" onClick={() => removeDet(i)} className="text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="p-2 border-t border-slate-200 flex items-center justify-between">
                  <Button type="button" onClick={addDet} variant="outline" size="sm" className="h-7 text-[12px]"><Plus className="w-3 h-3 mr-1" /> Línea</Button>
                  <div className="text-[12px] tabular flex gap-4">
                    <span>Debe: <strong>S/ {fmt(totales.debe)}</strong></span>
                    <span>Haber: <strong>S/ {fmt(totales.haber)}</strong></span>
                    <span className={Math.abs(totales.diff) < 0.01 ? "text-emerald-600" : "text-rose-600"}>Δ: <strong>S/ {fmt(totales.diff)}</strong></span>
                  </div>
                </div>
              </div>
              <DialogFooter><Button type="submit" className="bg-slate-900 text-white">Guardar asiento</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-slate-200 rounded-md">
        <table className="tbl w-full">
          <thead><tr><th>Fecha</th><th>Tipo</th><th>Glosa</th><th className="text-right">Debe</th><th className="text-right">Haber</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 ? <tr><td colSpan={6} className="text-center py-6 text-slate-500"><ScrollText className="w-8 h-8 mx-auto mb-2 text-slate-300" />Sin asientos</td></tr>
              : items.map((a) => (
                <Fragment key={a.id}>
                  <tr className="bg-slate-50">
                    <td className="mono font-bold">{a.fecha?.slice(0, 10)}</td>
                    <td><span className="badge badge-info">{a.tipo}</span></td>
                    <td className="font-medium text-slate-800">{a.glosa}</td>
                    <td className="tabular text-right font-bold">{fmt(a.detalles?.reduce((s, d) => s + d.debe, 0))}</td>
                    <td className="tabular text-right font-bold">{fmt(a.detalles?.reduce((s, d) => s + d.haber, 0))}</td>
                    <td className="text-right">{a.tipo === "MANUAL" && <button onClick={() => eliminar(a)} className="text-slate-600 hover:text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>}</td>
                  </tr>
                  {a.detalles?.map((d, i) => (
                    <tr key={a.id + "-" + i}>
                      <td></td>
                      <td className="mono text-slate-600">{d.cuenta_codigo}</td>
                      <td className="text-slate-600 text-[12px]">{d.glosa}</td>
                      <td className="tabular text-right text-slate-600">{d.debe > 0 ? fmt(d.debe) : ""}</td>
                      <td className="tabular text-right text-slate-600">{d.haber > 0 ? fmt(d.haber) : ""}</td>
                      <td></td>
                    </tr>
                  ))}
                </Fragment>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
